import { put, select } from "typed-redux-saga";
import { generateScoresCacheKey } from "../../utils/utils";
import MathUtils from "../../utils/MathUtils";
import ColumnUtils from "../../utils/ColumnUtils";
import RowUtils from "../../utils/RowUtils";
import { workerActions } from "../../reducers/worker.slice";
import {
  selectCorrelations,
  selectLinearRegression,
} from "../../selectors/worker.selector";
import ArrayUtils from "../../utils/ArrayUtils";
import { selectLeftPanelState, selectRootState } from "../../selectors";
import { setStatus } from "../../dom-utils";

function* generateCorrelations() {
  try {
    const { rows } = yield* select(selectRootState);
    const { selectedDataset, selectedMethod, selectedColumn } = yield* select(
      selectLeftPanelState
    );

    if (!selectedDataset || !selectedMethod || !selectedColumn) {
      setStatus("No method or column selected");
      return;
    }

    const key = generateScoresCacheKey(
      selectedDataset,
      selectedMethod,
      selectedColumn
    );

    const linearRegression = yield* select((state) =>
      selectLinearRegression(state, key)
    );

    const correlations = yield* select((state) =>
      selectCorrelations(state, key)
    );

    const hasDCor = ArrayUtils.isDefined([
      correlations?.dCor_x,
      correlations?.dCor_y,
      correlations?.dCor_z,
    ]);

    const hasTau = ArrayUtils.isDefined([
      correlations?.tau_xb,
      correlations?.tau_yb,
      correlations?.tau_zb,
    ]);

    const hasRSquareAndRho = ArrayUtils.isDefined([
      correlations?.r_sq_x,
      correlations?.r_sq_y,
      correlations?.r_sq_z,
      correlations?.rho_x,
      correlations?.rho_y,
      correlations?.rho_z,
    ]);

    if (hasDCor && linearRegression && hasTau && hasRSquareAndRho) {
      return;
    }

    console.info("Generating correlations ... ");

    const { Y: methodNameY, Z: methodNameZ } =
      ColumnUtils.getMethodColumnYZNames(selectedMethod);

    const result = RowUtils.getValues(rows, [
      selectedMethod,
      methodNameY,
      methodNameZ,
      selectedColumn,
    ]);

    const X = result[0];
    const Y = result[1];
    const Z = result[2];
    const V = result[3];

    // check if length of X, Y, Z, V are equal
    if (
      X.length !== Y.length ||
      Y.length !== Z.length ||
      Z.length !== V.length
    ) {
      throw new Error("Length of X, Y, Z, V are not equal");
    }

    if (!hasRSquareAndRho) {
      const variance = MathUtils.getVariance(X, Y, Z, V);
      const rho = MathUtils.getRho(X, Y, Z, V);

      yield* put(
        workerActions.setCorrelations({
          key,
          correlations: {
            ...variance,
            ...rho,
          },
        })
      );
    }

    if (!hasTau) {
      yield* put(workerActions.calculateTau({ key, X, Y, Z, V }));
    }

    if (!linearRegression) {
      const trainingData = X.map((x, i) => [x, Y[i], Z[i], V[i]]);
      yield* put(workerActions.linearRegression({ key, trainingData }));
    }

    if (!hasDCor) {
      yield* put(workerActions.calculateDCorX({ key, X, Y: V }));
      yield* put(workerActions.calculateDCorY({ key, X: Y, Y: V }));
      yield* put(workerActions.calculateDCorZ({ key, X: Z, Y: V }));
    }
  } catch (e) {
    console.error(e);
  }
}

export default generateCorrelations;
